import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import { Button } from '@/components/ui/Buttons';
import setupCsrfToken from '@/modules/SetupCSRFToken';

interface FailingScore {
  comment: string | null;
  name: string;
  value: number;
}

export interface Proposal {
  checkSummary: string | null;
  createdAt: string;
  currentBody: string | null;
  failingScores: FailingScore[];
  gradeBand: string;
  id: number;
  proposedBody: string | null;
  requestedBy: string | null;
  reviewNotes: string | null;
  reviewedBy: string | null;
  sectionTitle: string | null;
  stale: boolean;
  status: string;
}

const scoreLabel = (name: string) => name
  .replace(/^lr-/, '')
  .split('-')
  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

const STATUS_CHIP: Record<string, string> = {
  approved: 'ok',
  error: 'fail',
  failed_check: 'fail',
  pending: 'mid',
  rejected: 'fail',
  stale: 'off',
};

export interface DiffPart {
  text: string;
  type: 'added' | 'removed' | 'same';
}

// Mirrors the server's sentence extraction (per top-level block, then sentence-split) so the
// diff shown matches the granularity of the "N of M sentences changed" guard.
export const htmlSentences = (html: string): string[] => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.body.childNodes)
    .map(node => (node.textContent || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .flatMap(block => block.split(/(?<=[.!?])\s+/))
    .map(sentence => sentence.trim())
    .filter(Boolean);
};

// Sentence-level LCS diff — bodies are a few dozen sentences, so O(n*m) is nothing.
export const sentenceDiff = (currentHtml: string, proposedHtml: string): DiffPart[] => {
  const a = htmlSentences(currentHtml);
  const b = htmlSentences(proposedHtml);
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const parts: DiffPart[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      parts.push({ text: a[i], type: 'same' });
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      parts.push({ text: a[i], type: 'removed' });
      i += 1;
    } else {
      parts.push({ text: b[j], type: 'added' });
      j += 1;
    }
  }
  while (i < a.length) {
    parts.push({ text: a[i], type: 'removed' });
    i += 1;
  }
  while (j < b.length) {
    parts.push({ text: b[j], type: 'added' });
    j += 1;
  }
  return parts;
};

const DIFF_PART_STYLES: Record<DiffPart['type'], React.CSSProperties> = {
  added: { background: '#e9f4e9', borderRadius: 3, color: '#0b5d0b', padding: '1px 3px' },
  removed: {
    background: '#f9dede',
    borderRadius: 3,
    color: '#8f1f1f',
    padding: '1px 3px',
  },
  same: {},
};

interface ProposalDiffProps {
  currentHtml: string;
  proposedHtml: string;
}

const ProposalDiff = ({ currentHtml, proposedHtml }: ProposalDiffProps) => {
  const parts = sentenceDiff(currentHtml, proposedHtml);

  return (
    <div>
      <div style={{ color: '#52514e', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
        Changes
        <span style={{ fontWeight: 400, marginLeft: 12 }}>
          <span style={DIFF_PART_STYLES.removed}>removed</span>
          {' '}
          <span style={DIFF_PART_STYLES.added}>added</span>
        </span>
      </div>
      <div
        style={{
          background: '#fff',
          border: '1px solid #e6e4dd',
          borderRadius: 8,
          fontSize: 13,
          lineHeight: 1.7,
          maxHeight: 340,
          overflowY: 'auto',
          padding: 12,
        }}
      >
        {parts.map((part, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={`${index}-${part.type}`} style={DIFF_PART_STYLES[part.type]}>
            {part.text}
            {' '}
          </span>
        ))}
      </div>
    </div>
  );
};

interface PendingProposalProps {
  onReviewed: (proposal: Proposal) => void;
  proposal: Proposal;
}

const PendingProposal = ({ onReviewed, proposal }: PendingProposalProps) => {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const review = (action: 'approve' | 'reject') => {
    setupCsrfToken();
    setSubmitting(true);
    Axios.patch<Proposal>(`/admin/leveled_reading_correction_proposals/${proposal.id}/${action}`, { notes })
      .then(res => onReviewed(res.data))
      .catch((error) => {
        // 409 = the reading level changed under us; onReviewed swaps this card for the stale
        // banner, so leave submitting as-is. Only re-enable the buttons on a retryable error.
        if (error.response?.status === 409) {
          onReviewed(error.response.data);
        } else {
          setSubmitting(false);
        }
      });
  };

  return (
    <div className="card" id={`proposal-${proposal.id}`} style={{ marginBottom: 12, padding: 16 }}>
      <div style={{ alignItems: 'baseline', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <strong>{proposal.sectionTitle}</strong>
        <code>{proposal.gradeBand}</code>
        <span style={{ color: '#8a887f', fontSize: 12 }}>
          requested by {proposal.requestedBy || '—'} · {new Date(proposal.createdAt).toLocaleString()}
        </span>
        {proposal.checkSummary && (
          <span style={{ color: '#8a887f', fontSize: 12 }}>{proposal.checkSummary}</span>
        )}
      </div>
      <div style={{ margin: '12px 0' }}>
        <div style={{ color: '#52514e', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
          Flagged issues
          <span style={{ color: '#8a887f', fontWeight: 400, marginLeft: 8 }}>
            judge findings this draft is meant to fix
          </span>
        </div>
        {proposal.failingScores.map(score => (
          <div
            key={score.name}
            style={{
              background: '#fffbfa',
              border: '1px solid #f0ded6',
              borderLeft: '3px solid #d03b3b',
              borderRadius: '0 8px 8px 0',
              marginBottom: 6,
              maxWidth: 900,
              padding: '10px 14px',
            }}
          >
            <div style={{ alignItems: 'center', display: 'flex', gap: 8 }}>
              <span className={`chip ${Math.round(score.value) === 0 ? 'fail' : 'low'}`}>
                {Math.round(score.value)}
              </span>
              <strong style={{ color: '#8f1f1f', fontSize: 12.5 }}>{scoreLabel(score.name)}</strong>
            </div>
            {score.comment && (
              <p style={{ color: '#52514e', fontSize: 13, lineHeight: 1.55, margin: '6px 0 0' }}>
                {score.comment}
              </p>
            )}
          </div>
        ))}
      </div>
      {proposal.stale && (
        <div style={{ color: '#8f1f1f', fontSize: 13, marginBottom: 8 }}>
          The reading level changed after this draft was created — approving is disabled; reject and re-request.
        </div>
      )}
      <div style={{ marginBottom: 10 }}>
        <ProposalDiff currentHtml={proposal.currentBody || ''} proposedHtml={proposal.proposedBody || ''} />
      </div>
      <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <input
          placeholder="Review notes (optional)"
          style={{ flex: 1, minWidth: 220 }}
          type="text"
          value={notes}
          onChange={event => setNotes(event.target.value)}
        />
        <Button small disabled={submitting || proposal.stale} onClick={() => review('approve')}>
          Approve &amp; apply
        </Button>
        <Button outlineStyle small disabled={submitting} onClick={() => review('reject')}>
          Reject
        </Button>
      </div>
    </div>
  );
};

const REVIEWED_STATUSES = ['approved', 'rejected'];

interface ReviewHistoryProps {
  proposals: Proposal[];
}

// Human decisions only — approvals and rejections with who made them and why.
const ReviewHistory = ({ proposals }: ReviewHistoryProps) => {
  if (proposals.length === 0) return <p className="note">No reviewed proposals yet.</p>;

  return (
    <div className="tablebox">
      <table cellPadding={0} cellSpacing={0} className="tabular_data">
        <thead>
          <tr>
            <th>Section</th>
            <th>Band</th>
            <th>Decision</th>
            <th>Detail</th>
            <th>Reviewed by</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {proposals.map(proposal => (
            <tr key={proposal.id} id={`proposal-${proposal.id}`}>
              <td>{proposal.sectionTitle}</td>
              <td><code>{proposal.gradeBand}</code></td>
              <td><span className={`chip ${STATUS_CHIP[proposal.status] || 'mid'}`}>{proposal.status}</span></td>
              <td style={{ fontSize: 12 }}>{proposal.checkSummary}</td>
              <td style={{ fontSize: 12 }}>{proposal.reviewedBy || '—'}</td>
              <td style={{ fontSize: 12 }}>{proposal.reviewNotes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface DiscardedDraftsProps {
  proposals: Proposal[];
}

// Machine outcomes — drafts the verifier rejected before they ever reached a human. Nothing
// here is actionable on this page, so the styling stays muted and the copy says so.
const DiscardedDrafts = ({ proposals }: DiscardedDraftsProps) => {
  if (proposals.length === 0) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ color: '#8a887f' }}>Discarded draft attempts ({proposals.length})</h3>
      <p className="note" style={{ marginTop: 2 }}>
        The verifier rejected these AI drafts before review — there is nothing to approve or
        reject. Re-request a draft from the eval dashboard, or regenerate the section if the
        issue is broad (e.g. vocabulary).
      </p>
      <div className="tablebox">
        <table cellPadding={0} cellSpacing={0} className="tabular_data">
          <thead>
            <tr>
              <th>Section</th>
              <th>Band</th>
              <th>Requested</th>
              <th>Why it was discarded</th>
            </tr>
          </thead>
          <tbody>
            {proposals.map(proposal => (
              <tr key={proposal.id} id={`proposal-${proposal.id}`}>
                <td style={{ color: '#52514e' }}>{proposal.sectionTitle}</td>
                <td><code>{proposal.gradeBand}</code></td>
                <td style={{ fontSize: 12 }}>{new Date(proposal.createdAt).toLocaleDateString()}</td>
                <td style={{ fontSize: 12 }}>{proposal.checkSummary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface LeveledReadingCorrectionsProps {
  indexUrl: string;
}

const LeveledReadingCorrections = ({ indexUrl }: LeveledReadingCorrectionsProps) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState<Proposal[]>([]);

  useEffect(() => {
    Axios.get<{ proposals: Proposal[] }>(indexUrl)
      .then(res => setProposals(res.data.proposals))
      .catch(() => setError('Failed to load correction proposals.'))
      .finally(() => setLoading(false));
  }, [indexUrl]);

  // Deep links from the dashboard arrive before the async load, so the browser's native anchor
  // scroll finds nothing — re-run it once the proposals render.
  useEffect(() => {
    if (loading || !window.location.hash.startsWith('#proposal-')) return;

    document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [loading]);

  const replaceProposal = (updated: Proposal) => {
    setProposals(prev => prev.map(p => (p.id === updated.id ? updated : p)));
  };

  if (loading) return <p className="note">Loading correction proposals…</p>;
  if (error) return <p className="note" style={{ color: '#d03b3b' }}>{error}</p>;

  const pending = proposals.filter(p => p.status === 'pending');
  const reviewed = proposals.filter(p => REVIEWED_STATUSES.includes(p.status));
  const discarded = proposals.filter(p => p.status !== 'pending' && !REVIEWED_STATUSES.includes(p.status));

  return (
    <div>
      <h2>Pending review ({pending.length})</h2>
      {pending.length === 0 && <div className="card empty-state">Nothing awaiting review.</div>}
      {pending.map(proposal => (
        <PendingProposal key={proposal.id} proposal={proposal} onReviewed={replaceProposal} />
      ))}

      <h2>Review history</h2>
      <ReviewHistory proposals={reviewed} />
      <DiscardedDrafts proposals={discarded} />
    </div>
  );
};

export default LeveledReadingCorrections;
